/**
 * Date preset calculations for the Margins filter rail (Sprint 09 Item 1).
 *
 * Pure functions — no React, no Date side effects beyond reading
 * "today". Each preset returns ISO date strings (`YYYY-MM-DD`) the
 * `/api/reports/margins` endpoint already accepts as `date_from` /
 * `date_to`. URL persistence uses the `value` field (`?range=this_month`).
 */

export type PresetValue =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'ytd'

export interface DatePreset {
  value: PresetValue
  label: string
}

export const DATE_PRESETS: readonly DatePreset[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'ytd', label: 'YTD' },
] as const

export interface DateRange {
  from: string
  to: string
}

function iso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

/** First day of the ISO week (Monday). Pure function — does not mutate input. */
function startOfWeek(d: Date): Date {
  const out = new Date(d)
  // getDay(): 0=Sun..6=Sat. Shift so Mon=0..Sun=6.
  const dow = (out.getDay() + 6) % 7
  out.setDate(out.getDate() - dow)
  return out
}

/** Compute the date range for a preset, anchored to `now`. */
export function rangeForPreset(preset: PresetValue, now: Date = new Date()): DateRange {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  switch (preset) {
    case 'today':
      return { from: iso(today), to: iso(today) }
    case 'yesterday': {
      const y = new Date(today)
      y.setDate(y.getDate() - 1)
      return { from: iso(y), to: iso(y) }
    }
    case 'this_week': {
      const start = startOfWeek(today)
      return { from: iso(start), to: iso(today) }
    }
    case 'last_week': {
      const thisStart = startOfWeek(today)
      const lastEnd = new Date(thisStart)
      lastEnd.setDate(lastEnd.getDate() - 1)
      const lastStart = startOfWeek(lastEnd)
      return { from: iso(lastStart), to: iso(lastEnd) }
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      return { from: iso(start), to: iso(today) }
    }
    case 'last_month': {
      const lastEnd = new Date(today.getFullYear(), today.getMonth(), 0)
      const lastStart = new Date(lastEnd.getFullYear(), lastEnd.getMonth(), 1)
      return { from: iso(lastStart), to: iso(lastEnd) }
    }
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3)
      const start = new Date(today.getFullYear(), q * 3, 1)
      return { from: iso(start), to: iso(today) }
    }
    case 'ytd': {
      const start = new Date(today.getFullYear(), 0, 1)
      return { from: iso(start), to: iso(today) }
    }
  }
}

export function isPresetValue(s: string): s is PresetValue {
  return DATE_PRESETS.some((p) => p.value === s)
}
